import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { 
  Users, Award, Euro, Crown, Target, 
  ChevronRight, Network, Star, Zap, Copy, Check
} from 'lucide-react'
import { useAuthStore } from '@/stores/auth.store'
import { toast } from 'sonner'
import { NetworkTree } from '@/components/ixen/NetworkTree'
import { OrgChartTree } from '@/components/ixen/OrgChartTree'

interface DashboardData {
  utente: {
    id: number
    nome: string
    email: string
    tipo: string
    livello: string
    percentuale_livello: number
    attivo: boolean
    zero_canone_attivo: boolean
    data_attivazione: string | null
  }
  statistiche: {
    apc_diretti: number
    apc_rete: number
    volume_euro: number
  }
  progresso: {
    livello_attuale: string
    vq_attuale: number
    prossimo_livello: {
      codice: string
      nome: string
      soglia_apc: number
      percentuale: number
    } | null
    apc_mancanti: number
    percentuale_completamento: number
  }
  zero_canone: {
    attivo: boolean
    apc_diretti: number
    richiesti: number
    mancanti: number
    percentuale_completamento: number
    mese_riferimento?: string
  }
  commissioni: {
    mese_corrente: {
      dirette: number
      rete: number
      totale: number
      stato: string | null
    }
  }
}

interface Livello {
  codice: string
  nome: string
  percentuale: number
  soglia_apc: number
  raggiunto: boolean
  corrente: boolean
  prossimo: boolean
  apc_mancanti?: number
  percentuale_completamento?: number
}

interface MembroAlbero {
  id: number
  email: string
  nome: string
  tipo: string
  livello: string
  percentuale: number
  attivo: boolean
  sponsor_id: number | null
  depth: number
  stato: 'apc' | 'zero_canone' | 'inattivo' | 'attivo'
  is_apc: boolean
  in_zero_canone: boolean
  vq_rete: number
}

interface ReteAlbero {
  membri: MembroAlbero[]
  percentuale_utente: number
  totale_membri: number
  totale_apc: number
  totale_zero_canone: number
}

const LIVELLI_INFO: Record<string, { icon: React.ReactNode; color: string }> = {
  'L0': { icon: <Target className="w-4 h-4" />, color: 'bg-gray-600' },
  'L1': { icon: <Star className="w-4 h-4" />, color: 'bg-slate-500' },
  'L2': { icon: <Star className="w-4 h-4" />, color: 'bg-slate-400' },
  'L3': { icon: <Zap className="w-4 h-4" />, color: 'bg-blue-500' },
  'L4': { icon: <Zap className="w-4 h-4" />, color: 'bg-blue-400' },
  'L5': { icon: <Award className="w-4 h-4" />, color: 'bg-purple-500' },
  'L6': { icon: <Award className="w-4 h-4" />, color: 'bg-purple-400' },
  'L7': { icon: <Crown className="w-4 h-4" />, color: 'bg-amber-500' },
  'L8': { icon: <Crown className="w-4 h-4" />, color: 'bg-amber-400' },
  'L9': { icon: <Crown className="w-4 h-4" />, color: 'bg-orange-500' },
  'L10': { icon: <Crown className="w-4 h-4" />, color: 'bg-red-500' },
  'L11': { icon: <Crown className="w-4 h-4" />, color: 'bg-pink-500' },
  'L12': { icon: <Crown className="w-4 h-4" />, color: 'bg-rose-500' },
  'L13': { icon: <Crown className="w-4 h-4" />, color: 'bg-gradient-to-r from-yellow-400 to-amber-500' },
}

export function IxenDashboard() {
  const { token } = useAuthStore()
  const [loading, setLoading] = useState(true)
  const [dashboard, setDashboard] = useState<DashboardData | null>(null)
  const [livelli, setLivelli] = useState<Livello[]>([])
  const [reteAlbero, setReteAlbero] = useState<ReteAlbero | null>(null)
  const [copied, setCopied] = useState(false)
  
  useEffect(() => {
    fetchDashboard()
    fetchLivelli()
    fetchReteAlbero()
  }, [])
  
  const fetchDashboard = async () => {
    try {
      const res = await fetch('/api/v1/ixen/dashboard', {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setDashboard(data)
      }
    } catch (error) {
      console.error('Error fetching iXen dashboard:', error)
    } finally {
      setLoading(false)
    }
  }
  
  const fetchLivelli = async () => {
    try {
      const res = await fetch('/api/v1/ixen/livelli', {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setLivelli(data.livelli || [])
      }
    } catch (error) {
      console.error('Error fetching livelli:', error)
    }
  }
  
  const fetchReteAlbero = async () => {
    try {
      const res = await fetch('/api/v1/ixen/rete/albero', {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setReteAlbero(data)
      }
    } catch (error) {
      console.error('Error fetching rete albero:', error)
    }
  }
  
  const copyReferralLink = () => {
    const link = `${window.location.origin}/register?ref=${dashboard?.utente.referral_code}`
    navigator.clipboard.writeText(link)
    setCopied(true)
    toast.success('Link di invito copiato!')
    setTimeout(() => setCopied(false), 2000)
  }
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    )
  }
  
  if (!dashboard) {
    return (
      <div className="p-6">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-8 text-center">
            <Network className="w-16 h-16 mx-auto mb-4 text-slate-500" />
            <h2 className="text-xl font-semibold mb-2">Non sei ancora iscritto a iXen</h2>
            <p className="text-slate-400 mb-6">
              Attiva la tua licenza per accedere al network marketing iXen
            </p>
            <Button className="bg-blue-600 hover:bg-blue-700">
              Attiva Licenza iXen
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }
  
  const levelInfo = LIVELLI_INFO[dashboard.utente.livello] || LIVELLI_INFO['L1']
  
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Dashboard iXen</h1>
          <p className="text-slate-400">Il tuo network marketing personale</p>
        </div>
        <Button 
          onClick={copyReferralLink}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
          Copia Link Invito
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Livello Attuale</p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={`${levelInfo.color} text-white`}>
                    {levelInfo.icon}
                    <span className="ml-1">{dashboard.utente.livello}</span>
                  </Badge>
                  <span className="text-lg font-bold">{dashboard.utente.percentuale_livello}%</span>
                </div>
              </div>
              <Award className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">APC Diretti</p>
                <p className="text-2xl font-bold">{dashboard.statistiche.apc_diretti}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">VQ Rete (APC Totali)</p>
                <p className="text-2xl font-bold">{dashboard.statistiche.apc_rete}</p>
              </div>
              <Network className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Commissioni Mese</p>
                <p className="text-2xl font-bold text-green-500">
                  {dashboard.commissioni.mese_corrente.totale.toFixed(2)}
                </p>
              </div>
              <Euro className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Progresso Livello
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">VQ Attuale</span>
                <span className="font-medium">{dashboard.progresso.vq_attuale} APC</span>
              </div>
              
              <Progress 
                value={dashboard.progresso.percentuale_completamento} 
                className="h-3"
              />
              
              {dashboard.progresso.prossimo_livello && (
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <ChevronRight className="w-4 h-4 text-blue-400" />
                    <span className="text-sm font-medium">
                      Prossimo: {dashboard.progresso.prossimo_livello.nome}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400">
                    Mancano {dashboard.progresso.apc_mancanti} APC 
                    ({dashboard.progresso.prossimo_livello.percentuale}% commissioni)
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5" />
              Zero Canone
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Stato</span>
                <Badge className={dashboard.zero_canone.attivo ? 'bg-green-600' : 'bg-slate-600'}>
                  {dashboard.zero_canone.attivo ? 'ATTIVO' : 'NON ATTIVO'}
                </Badge>
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">APC Diretti</span>
                <span className="font-medium">
                  {dashboard.zero_canone.apc_diretti} / {dashboard.zero_canone.richiesti}
                </span>
              </div>
              
              <Progress 
                value={dashboard.zero_canone.percentuale_completamento} 
                className="h-3"
              />
              
              {dashboard.zero_canone.mese_riferimento && (
                <p className="text-xs text-slate-500">
                  Basato su {dashboard.zero_canone.mese_riferimento}
                </p>
              )}
              {!dashboard.zero_canone.attivo && (
                <p className="text-xs text-slate-400">
                  Porta {dashboard.zero_canone.mancanti} APC diretti per azzerare il canone mensile
                </p>
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Euro className="w-5 h-5" />
              Commissioni Mese
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-400">Dirette (10%)</span>
                <span className="font-medium">{dashboard.commissioni.mese_corrente.dirette.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Rete (differenziale)</span>
                <span className="font-medium">{dashboard.commissioni.mese_corrente.rete.toFixed(2)}</span>
              </div>
              <hr className="border-slate-700" />
              <div className="flex justify-between text-lg">
                <span className="font-medium">Totale</span>
                <span className="font-bold text-green-500">
                  {dashboard.commissioni.mese_corrente.totale.toFixed(2)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Tabs defaultValue="orgchart" className="w-full">
        <TabsList className="bg-slate-800">
          <TabsTrigger value="orgchart">Org Chart</TabsTrigger>
          <TabsTrigger value="rete">Lista</TabsTrigger>
          <TabsTrigger value="livelli">Piano Carriera</TabsTrigger>
        </TabsList>

        <TabsContent value="orgchart" className="mt-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Network className="w-5 h-5" />
                Org Chart - Visualizzazione Grafica
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reteAlbero && dashboard ? (
                <OrgChartTree
                  membri={reteAlbero.membri}
                  rootNome={dashboard.utente.nome}
                  rootLivello={dashboard.utente.livello}
                  rootPercentuale={dashboard.utente.percentuale_livello}
                />
              ) : (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rete" className="mt-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Network className="w-5 h-5" />
                La Mia Rete - Lista
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reteAlbero ? (
                <NetworkTree 
                  membri={reteAlbero.membri}
                  percentualeUtente={reteAlbero.percentuale_utente}
                />
              ) : (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="livelli" className="mt-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle>Piano Carriera - 13 Livelli</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {livelli.map((livello) => {
                  const info = LIVELLI_INFO[livello.codice] || LIVELLI_INFO['L1']
                  return (
                    <div 
                      key={livello.codice}
                      className={`flex items-center gap-4 p-3 rounded-lg transition-colors
                        ${livello.corrente ? 'bg-blue-600/20 border border-blue-500' : 
                          livello.raggiunto ? 'bg-green-600/10' : 'bg-slate-700/30'}
                      `}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${info.color}`}>
                        {info.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{livello.nome}</span>
                          {livello.corrente && (
                            <Badge className="bg-blue-600 text-xs">TU SEI QUI</Badge>
                          )}
                          {livello.raggiunto && !livello.corrente && (
                            <Check className="w-4 h-4 text-green-500" />
                          )}
                        </div>
                        <p className="text-sm text-slate-400">
                          {livello.soglia_apc} APC - {livello.percentuale}% commissioni
                        </p>
                      </div>
                      {livello.prossimo && (
                        <div className="text-right">
                          <p className="text-sm text-blue-400">Prossimo obiettivo</p>
                          <p className="text-xs text-slate-400">
                            -{livello.apc_mancanti} APC
                          </p>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
